import type {
  Chapter,
  EpisodeDto,
  FeedDto,
  LiveItemDto,
  NormalizedTranscript,
  OpmlImportResult,
  PersonDetailDto,
  PlaylistDetailDto,
  PlaylistSummaryDto,
  ProgressDto,
  PushSubscriptionDto,
  PushSubscriptionPayload,
  QueueItemDto,
  SearchResultDto,
  Transcript,
  UserDto,
  UserSettings,
} from '../shared/types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    // Sessions are HttpOnly cookies, so every call must carry credentials.
    credentials: 'same-origin',
    headers:
      init.body && !(init.headers as Record<string, string> | undefined)?.['Content-Type']
        ? { 'Content-Type': 'application/json', ...(init.headers ?? {}) }
        : init.headers,
    ...init,
  });

  if (res.status === 204) return undefined as T;

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new ApiError(
      res.status,
      err?.error ?? 'error',
      err?.message ?? `Request failed (${res.status})`,
    );
  }
  return body as T;
}

const json = (data: unknown): RequestInit => ({ body: JSON.stringify(data) });

export const api = {
  // ---------------------------------------------------------------------- session
  me: () => request<{ user: UserDto | null }>('/api/me'),

  updateSettings: (patch: UserSettings) =>
    request<{ user: UserDto }>('/api/me/settings', { method: 'PATCH', ...json(patch) }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  /** Irreversible: removes the account and every row belonging to it. */
  deleteAccount: () => request<{ ok: true }>('/api/me', { method: 'DELETE' }),

  requestMagicLink: (email: string) =>
    request<{ ok: true; devLink?: string }>('/api/auth/magic/request', {
      method: 'POST',
      ...json({ email }),
    }),

  passkeyRegisterOptions: (payload: { email?: string; displayName?: string }) =>
    request<{ handle: string; options: PublicKeyCredentialCreationOptionsJSON }>(
      '/api/auth/passkey/register/options',
      { method: 'POST', ...json(payload) },
    ),

  passkeyRegisterVerify: (payload: { handle: string; response: unknown }) =>
    request<{ user: UserDto }>('/api/auth/passkey/register/verify', {
      method: 'POST',
      ...json(payload),
    }),

  passkeyLoginOptions: () =>
    request<{ handle: string; options: PublicKeyCredentialRequestOptionsJSON }>(
      '/api/auth/passkey/login/options',
      { method: 'POST' },
    ),

  passkeyLoginVerify: (payload: { handle: string; response: unknown }) =>
    request<{ user: UserDto }>('/api/auth/passkey/login/verify', {
      method: 'POST',
      ...json(payload),
    }),

  passwordRegister: (payload: { email: string; password?: string; displayName?: string }) =>
    request<{ user: UserDto }>('/api/auth/password/register', {
      method: 'POST',
      ...json(payload),
    }),

  passwordLogin: (payload: { email: string; password?: string }) =>
    request<{ user: UserDto }>('/api/auth/password/login', {
      method: 'POST',
      ...json(payload),
    }),

  // ------------------------------------------------------------------------ feeds
  search: (q: string) =>
    request<{ results: SearchResultDto[]; isUrl?: boolean }>(
      `/api/feeds/search?q=${encodeURIComponent(q)}`,
    ),

  addFeed: (feedUrl: string) =>
    request<{ feed: FeedDto }>('/api/feeds', { method: 'POST', ...json({ feedUrl }) }),

  feed: (id: number, opts?: { signal?: AbortSignal }) =>
    request<{ feed: FeedDto; episodes: EpisodeDto[]; nextCursor: number | null }>(
      `/api/feeds/${id}`,
      opts?.signal ? { signal: opts.signal } : undefined,
    ),

  feedEpisodes: (
    id: number,
    before?: number | null,
    limit = 30,
    opts?: { signal?: AbortSignal },
  ) =>
    request<{ episodes: EpisodeDto[]; nextCursor: number | null }>(
      `/api/feeds/${id}/episodes?limit=${limit}${before ? `&before=${before}` : ''}`,
      opts?.signal ? { signal: opts.signal } : undefined,
    ),

  refreshFeed: (id: number, opts?: { signal?: AbortSignal }) =>
    request<unknown>(`/api/feeds/${id}/refresh`, {
      method: 'POST',
      ...(opts?.signal ? { signal: opts.signal } : {}),
    }),

  subscribe: (feedId: number) =>
    request<{ ok: true }>(`/api/subscriptions/${feedId}`, { method: 'POST' }),

  unsubscribe: (feedId: number) =>
    request<{ ok: true }>(`/api/subscriptions/${feedId}`, { method: 'DELETE' }),

  subscriptions: () =>
    request<{ subscriptions: (FeedDto & { subscribedAt: number })[] }>('/api/me/subscriptions'),

  exportOpmlUrl: () => '/api/me/export/opml',

  importOpml: (opml: string) =>
    request<OpmlImportResult>('/api/me/import/opml', {
      method: 'POST',
      ...json({ opml }),
    }),

  continueListening: () => request<{ episodes: EpisodeDto[] }>('/api/me/continue'),

  inbox: (before?: number) =>
    request<{ episodes: EpisodeDto[]; nextCursor: number | null }>(
      before !== undefined ? `/api/me/inbox?before=${before}` : '/api/me/inbox',
    ),

  // --------------------------------------------------------------------- episodes
  episode: (id: number) =>
    request<{ episode: EpisodeDto; fullContentHtml: string | null }>(`/api/episodes/${id}`),

  transcript: (id: number, url?: string) =>
    request<{ transcript: NormalizedTranscript | null; available: Transcript[] }>(
      `/api/episodes/${id}/transcript${url ? `?url=${encodeURIComponent(url)}` : ''}`,
    ),

  chapters: (id: number) => request<{ chapters: Chapter[] }>(`/api/episodes/${id}/chapters`),

  // --------------------------------------------------------------------- progress
  putProgress: (
    episodeId: number,
    payload: { position: number; duration?: number; completed?: boolean; rate?: number },
  ) =>
    request<{ progress: ProgressDto }>(`/api/progress/${episodeId}`, {
      method: 'PUT',
      ...json(payload),
    }),

  // ------------------------------------------------------------------------ queue
  queue: () => request<{ items: QueueItemDto[] }>('/api/queue'),

  enqueue: (episodeId: number, position: 'next' | 'last' = 'last') =>
    request<{ ok: true }>('/api/queue', { method: 'POST', ...json({ episodeId, position }) }),

  dequeue: (episodeId: number) =>
    request<{ ok: true }>(`/api/queue/${episodeId}`, { method: 'DELETE' }),

  reorderQueue: (payload: {
    episodeId: number;
    beforeEpisodeId: number | null;
    afterEpisodeId: number | null;
  }) => request<{ ok: true }>('/api/queue/reorder', { method: 'PATCH', ...json(payload) }),

  // -------------------------------------------------------------------- playlists
  playlists: () => request<{ playlists: PlaylistSummaryDto[] }>('/api/playlists'),

  playlist: (id: number) => request<PlaylistDetailDto>(`/api/playlists/${id}`),

  createPlaylist: (name: string) =>
    request<{ playlist: { id: number; name: string; createdAt: number } }>('/api/playlists', {
      method: 'POST',
      ...json({ name }),
    }),

  renamePlaylist: (id: number, name: string) =>
    request<{ ok: true }>(`/api/playlists/${id}`, {
      method: 'PATCH',
      ...json({ name }),
    }),

  deletePlaylist: (id: number) =>
    request<{ ok: true }>(`/api/playlists/${id}`, { method: 'DELETE' }),

  addToPlaylist: (id: number, episodeId: number) =>
    request<{ ok: true }>(`/api/playlists/${id}/items`, {
      method: 'POST',
      ...json({ episodeId }),
    }),

  removeFromPlaylist: (id: number, episodeId: number) =>
    request<{ ok: true }>(`/api/playlists/${id}/items/${episodeId}`, { method: 'DELETE' }),

  // ---------------------------------------------------------------------- persons
  person: (name: string) =>
    request<PersonDetailDto>(`/api/persons/${encodeURIComponent(name)}`),

  // ------------------------------------------------------------------------- push
  getVapidPublicKey: () => request<{ publicKey: string }>('/api/push/vapid-public-key'),

  registerPushSubscription: (data: PushSubscriptionPayload) =>
    request<{ ok: true; subscription: PushSubscriptionDto }>('/api/push/subscriptions', {
      method: 'POST',
      ...json(data),
    }),

  unregisterPushSubscription: (endpoint: string) =>
    request<{ ok: true }>('/api/push/subscriptions', {
      method: 'DELETE',
      ...json({ endpoint }),
    }),

  // ------------------------------------------------------------------------- live
  live: () => request<{ liveItems: LiveItemDto[]; items?: LiveItemDto[] }>('/api/live'),
};

/**
 * Best-effort progress write during page teardown.
 *
 * `sendBeacon` survives the page being discarded, which a `fetch` in `pagehide` does not.
 * It sends `text/plain`, which the API is written to tolerate.
 */
export function beaconProgress(
  episodeId: number,
  payload: { position: number; duration?: number; completed?: boolean; rate?: number },
): void {
  try {
    navigator.sendBeacon?.(`/api/progress/${episodeId}`, JSON.stringify(payload));
  } catch {
    /* teardown is not a place to surface errors */
  }
}

// SimpleWebAuthn's JSON option shapes, kept local so the browser bundle doesn't pull the
// whole library's type surface in.
export interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  [key: string]: unknown;
}
export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  rpId?: string;
  [key: string]: unknown;
}
